/**
 * dview:LinePositionViewer: where the entity lies on its line (board 6): the line from its
 * first to its last km with the entity marked, or, with
 * dview:show "share", the entity's share of the line.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { lineStops, peerContext, type LineStop, type PeerContext } from '@services/entityView.service';
import { setting, settingList, type ViewPropertyShape } from '@utils/viewShapes.utils';
import type { EntityViewModel } from '@hooks/useEntityView';
import { keyedValues } from '../page';
import { formatKm, num } from '../linearLocation';
import { useNumberFormat } from './figures';
import { uriText } from './values';
import { Chip } from './chip';
import type { Widget, WidgetContext } from './types';
import styles from './widgets.module.scss';

export interface LineContext {
  line: string;
  peers: PeerContext | null;
  stops: LineStop[];
}

const cache = new Map<string, Promise<LineContext>>();

/** The line the entity lies on (the row named by dview:among), with its extent and stations. */
export function useLineContext(model: EntityViewModel | undefined, predicate: string | undefined): LineContext | null {
  const line = predicate
    ? model?.allProperties
        .filter((property) => property.path.kind === 'predicate' && property.path.iri === predicate)
        .flatMap((property) => model.values[property.id] ?? [])
        .find((value) => value.type === 'uri')?.value
    : undefined;
  const classIri = model?.nodeShapes[0]?.targetClasses[0];
  const [context, setContext] = useState<LineContext | null>(null);
  useEffect(() => {
    if (!line || !classIri || !predicate) return;
    let cancelled = false;
    const key = `${classIri} ${line}`;
    if (!cache.has(key)) {
      cache.set(
        key,
        Promise.all([peerContext(classIri, predicate, line).catch(() => null), lineStops(line).catch(() => [])]).then(
          ([peers, stops]) => ({ line, peers, stops })
        )
      );
    }
    cache.get(key)!.then((result) => !cancelled && setContext(result));
    return () => {
      cancelled = true;
    };
  }, [line, classIri, predicate]);
  return context;
}

/** The first of the rows a setting names (space-separated keys) that has a number: topology before bno:atKm. */
export function firstKm(context: WidgetContext, property: ViewPropertyShape, name: string): number | null {
  const model = context.model;
  if (!model) return null;
  for (const key of settingList(property, name)) {
    const km = num(keyedValues(model, key));
    if (km !== null) return km;
  }
  return null;
}

export function trackKm(context: WidgetContext): [number, number] | null {
  const model = context.model;
  if (!model) return null;
  // The km rows named by dview:kmFrom / dview:kmTo: the first pair with values (a track's ends, or a spot object's km)
  for (const row of model.allProperties.filter((candidate) => setting(candidate, 'kmFrom') && setting(candidate, 'kmTo'))) {
    const from = firstKm(context, row, 'kmFrom');
    const to = firstKm(context, row, 'kmTo');
    if (from !== null && to !== null) return [Math.min(from, to), Math.max(from, to)];
  }
  return null;
}

/**
 * The whole line as an axis from its first to its last km, the ends named by the line's first and
 * last operational points, the entity marked with its km range under it. The nearest operational
 * points on either side are named in a caption, not on the axis.
 */
export function LineStrip({ line, track, context }: { line: LineContext; track: [number, number]; context: WidgetContext }): JSX.Element | null {
  const { t } = useTranslation();
  const format = useNumberFormat();
  const start = line.stops[0];
  const end = line.stops[line.stops.length - 1];
  const lo = Math.min(line.peers?.from ?? track[0], start?.km ?? track[0], track[0]);
  const hi = Math.max(line.peers?.to ?? track[1], end?.km ?? track[1], track[1]);
  if (hi <= lo) return null;
  const pct = (km: number) => ((km - lo) / (hi - lo)) * 100;
  const before = [...line.stops].reverse().find((stop) => stop.km <= track[0]);
  const after = line.stops.find((stop) => stop.km >= track[1]);
  const middle = Math.min(85, Math.max(15, pct((track[0] + track[1]) / 2)));
  const lineName = uriText(line.line, context);
  return (
    <span className={styles.lineStrip} role="img" aria-label={t('entity.template.line.aria', { line: lineName, from: formatKm(track[0]), to: formatKm(track[1]) })}>
      <span className={styles.lineEnds}>
        <span>
          {start?.label ?? lineName} <span className={styles.lineKm}>km {format(lo / 1000, 0)}</span>
        </span>
        <span>
          <span className={styles.lineKm}>km {format(hi / 1000, 0)}</span> {end?.label ?? ''}
        </span>
      </span>
      <span className={styles.lineBar} aria-hidden="true">
        <span className={styles.lineHere} style={{ left: `${pct(track[0])}%`, width: `${Math.max(1.5, pct(track[1]) - pct(track[0]))}%` }} />
      </span>
      <span className={styles.lineHereRow} aria-hidden="true">
        <span className={styles.lineHereLabel} style={{ left: `${middle}%` }}>
          ▲ km {formatKm(track[0])}–{formatKm(track[1])}
        </span>
      </span>
      {(before || after) && (
        <span className={styles.lineCaption}>
          {t('entity.template.line.between', { line: lineName, before: before?.label ?? '–', after: after?.label ?? '–' })}
        </span>
      )}
    </span>
  );
}

const LinePosition = ({ property, values, context }: Parameters<Widget>[0]): JSX.Element | null => {
  const { t } = useTranslation();
  const format = useNumberFormat();
  const line = useLineContext(context.model, setting(property, 'among'));
  const track = trackKm(context);
  const show = setting(property, 'show') ?? 'position';
  const lineName = line ? uriText(line.line, context) : '';

  if (show === 'share') {
    const length = num(keyedValues(context.model!, setting(property, 'value') ?? 'length')) ?? num(values);
    if (length === null) return null;
    const peers = line?.peers;
    const lineLength = peers && peers.from !== null && peers.to !== null ? (peers.to - peers.from) / 1000 : null;
    const share = lineLength ? (length / lineLength) * 100 : null;
    return (
      <span className={styles.share}>
        <span className={styles.shareValue}>{format(length, 2)} km</span>
        {context.pictures && share !== null && (
          <span className={styles.shareBar} aria-hidden="true">
            <span style={{ width: `${Math.max(1, Math.min(100, share))}%` }} />
          </span>
        )}
        {share !== null && (
          <span className={styles.symbolContext}>
            {t('entity.template.line.share', { share: format(share, share < 1 ? 1 : 0), line: lineName, length: format(lineLength!, 0) })}
          </span>
        )}
      </span>
    );
  }

  const iri = values.find((value) => value.type === 'uri')?.value;
  return (
    <span className={styles.linePosition}>
      {iri && <Chip iri={iri} icon="line" context={context} />}
      {context.pictures && line && track && <LineStrip line={line} track={track} context={context} />}
    </span>
  );
};

export const LinePositionViewer: Widget = (props) => <LinePosition {...props} />;
